(function () {
    'use strict';

    const modal = document.querySelector('[data-modal]');
    if (!modal) return;

    const bookingData = window.bookingModalData || { schedules: [] };
    let schedules = Array.isArray(bookingData.schedules) ? bookingData.schedules : [];

    // Pagination tracking
    let currentPage = 0;
    let totalPages = 1;
    let hasMorePages = true;
    let isLoadingSchedules = false;
    const loadedPages = new Set();

    let currentDate = new Date();
    let selectedDate = null;
    let selectedSlot = null;

    const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];

    function openModal() {
        modal.classList.remove('hidden');
        modal.classList.add('flex');
        document.body.style.overflow = 'hidden';
        renderCalendar();
    }

    function closeModal() {
        modal.classList.add('hidden');
        modal.classList.remove('flex');
        document.body.style.overflow = '';
        resetSelection();
    }

    function resetSelection() {
        selectedDate = null;
        selectedSlot = null;
        document.getElementById('slot-selection').classList.add('hidden');
        document.getElementById('slot-placeholder').classList.remove('hidden');
        document.getElementById('confirm-booking').disabled = true;
    }

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    function formatDate(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    function formatDateDisplay(dateStr) {
        const parts = dateStr.split('-');
        const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        const dayName = dayNames[date.getDay()];
        const monthName = monthNames[date.getMonth()];
        const day = date.getDate();
        const year = date.getFullYear();
        return `${dayName}, ${monthName} ${day}, ${year}`;
    }

    function parseDate(dateStr) {
        const parts = dateStr.split('-');
        return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    }

    /**
     * Show/hide loading indicator
     */
    function setLoading(loading) {
        const indicator = document.getElementById('loading-indicator');
        if (indicator) {
            if (loading) {
                indicator.classList.remove('hidden');
            } else {
                indicator.classList.add('hidden');
            }
        }
    }

    /**
     * Fetch a page of schedules from the Liquid API
     */
    async function fetchSchedulePage(pageNumber) {
        // Return if already loading or page already fetched
        if (isLoadingSchedules || loadedPages.has(pageNumber)) {
            return false;
        }

        isLoadingSchedules = true;
        setLoading(true);

        try {
            const url = pageNumber > 1
                ? `/pages/schedules-api?page=${pageNumber}`
                : '/pages/schedules-api';

            const response = await fetch(url);

            if (!response.ok) {
                console.error('Failed to fetch schedules:', response.statusText);
                return false;
            }

            const data = await response.json();

            if (!data.schedules || !data.pagination) {
                console.error('Invalid response format:', data);
                return false;
            }

            // Update pagination tracking
            currentPage = data.pagination.current_page;
            totalPages = data.pagination.total_pages;
            hasMorePages = data.pagination.has_next;
            loadedPages.add(pageNumber);

            // Merge new schedules with existing ones (avoid duplicates)
            data.schedules.forEach(newSchedule => {
                const exists = schedules.some(s =>
                    s.date === newSchedule.date && s.slot === newSchedule.slot
                );
                if (!exists) {
                    schedules.push(newSchedule);
                }
            });

            // Re-render calendar with new data
            renderCalendar();

            return true;

        } catch (error) {
            console.error('Error fetching schedules:', error);
            return false;
        } finally {
            isLoadingSchedules = false;
            setLoading(false);
        }
    }

    /**
     * Check if we have schedules for a given month, fetch next page if needed
     */
    async function ensureSchedulesForMonth(year, month) {
        const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;

        // Check if we already have schedules for this month
        const hasSchedulesForMonth = schedules.some(s => s.date.startsWith(monthKey));

        if (hasSchedulesForMonth) {
            return true;
        }

        // If no more pages to load, we don't have schedules for this month
        if (!hasMorePages && currentPage > 0) {
            return false;
        }

        // Fetch next page
        const nextPage = currentPage + 1;
        const success = await fetchSchedulePage(nextPage);

        if (success && hasMorePages) {
            // Recursively check again after loading
            return await ensureSchedulesForMonth(year, month);
        }

        // Check one more time if we now have schedules for this month
        return schedules.some(s => s.date.startsWith(monthKey));
    }

    function hasDateInSchedule(dateStr) {
        if (!schedules || schedules.length === 0) return false;
        return schedules.some((schedule) => schedule.date === dateStr);
    }

    function getAvailableSlots(dateStr) {
        if (!schedules || schedules.length === 0) {
            return [];
        }

        const dateSchedules = schedules.filter((schedule) => schedule.date === dateStr);

        if (dateSchedules.length === 0) {
            return [];
        }

        const slots = [
            { value: 'morning', label: 'Morning Flight', icon: '🌅' },
            { value: 'afternoon', label: 'Afternoon Flight', icon: '🌇' },
        ];

        const availableSlots = slots
            .map((slot) => {
                const scheduleEntry = dateSchedules.find((s) => s.slot === slot.value);
                if (!scheduleEntry) {
                    return null;
                }

                return {
                    ...slot,
                    available: !scheduleEntry.is_booked,
                };
            })
            .filter((slot) => slot !== null);

        return availableSlots;
    }

    async function renderCalendar() {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        document.getElementById('calendar-month-year').textContent = `${monthNames[month]} ${year}`;

        // Ensure we have schedules for this month
        if (currentPage === 0) {
            // First time - load initial page
            await fetchSchedulePage(1);
            return; // fetchSchedulePage will call renderCalendar again
        } else {
            // Check if we need to load more pages for this month
            await ensureSchedulesForMonth(year, month);
        }

        const firstDay = new Date(year, month, 1);
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const startingDayOfWeek = firstDay.getDay();

        const calendarGrid = document.getElementById('calendar-grid');
        calendarGrid.innerHTML = '';

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        for (let i = 0; i < startingDayOfWeek; i++) {
            const emptyDay = document.createElement('div');
            emptyDay.className = 'py-2';
            calendarGrid.appendChild(emptyDay);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            date.setHours(0, 0, 0, 0);
            const dateStr = formatDate(date);
            const isToday = date.getTime() === today.getTime();
            const isPast = date.getTime() < today.getTime();
            const isSelected = selectedDate === dateStr;
            const isAvailable = hasDateInSchedule(dateStr);

            const dayButton = document.createElement('button');
            dayButton.type = 'button';
            dayButton.className = `py-2 text-center rounded-full transition-colors ${isPast || !isAvailable
                ? 'text-gray-300 cursor-not-allowed'
                : isSelected
                    ? 'bg-brand-primary text-white font-semibold'
                    : isToday && isAvailable
                        ? 'text-brand-primary font-semibold hover:bg-gray-100'
                        : 'text-text-primary hover:bg-gray-100'
                }`;

            dayButton.innerHTML = `<time datetime="${dateStr}">${day}</time>`;

            if (!isPast && isAvailable) {
                dayButton.addEventListener('click', () => selectDate(dateStr));
            } else {
                dayButton.disabled = true;
            }

            calendarGrid.appendChild(dayButton);
        }
    }

    function selectDate(dateStr) {
        selectedDate = dateStr;
        selectedSlot = null;
        renderCalendar();
        renderSlots(dateStr);
    }

    function renderSlots(dateStr) {
        const slots = getAvailableSlots(dateStr);
        const slotsContainer = document.getElementById('slots-container');
        const slotSelection = document.getElementById('slot-selection');
        const slotPlaceholder = document.getElementById('slot-placeholder');
        const selectedDateDisplay = document.getElementById('selected-date-display');

        selectedDateDisplay.textContent = formatDateDisplay(dateStr);
        slotsContainer.innerHTML = '';

        if (slots.length === 0) {
            slotsContainer.innerHTML = `
        <div class="text-center py-8 text-gray-400">
          <p class="text-sm">No slots configured for this date</p>
        </div>
      `;
        } else {
            const hasAvailableSlots = slots.some((slot) => slot.available);

            // Always show individual slots, whether booked or available
            slots.forEach((slot) => {
                const slotButton = document.createElement('button');
                slotButton.type = 'button';
                slotButton.className = `w-full p-4 rounded-lg border-2 transition-all text-left ${!slot.available
                    ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                    : selectedSlot === slot.value
                        ? 'border-brand-primary bg-brand-primary bg-opacity-10'
                        : 'border-gray-200 hover:border-brand-primary'
                    }`;

                slotButton.innerHTML = `
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <span class="text-2xl ${!slot.available ? 'opacity-50' : ''}">${slot.icon}</span>
              <div>
                <div class="font-semibold ${!slot.available ? 'text-gray-400' : 'text-text-primary'}">${slot.label}</div>
                <div class="text-sm ${!slot.available ? 'text-gray-400' : 'text-gray-500'} mt-1">
                  ${slot.available ? 'Available' : 'Fully Booked'}
                </div>
              </div>
            </div>
            ${selectedSlot === slot.value
                        ? '<svg class="w-6 h-6 text-brand-primary" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>'
                        : !slot.available
                            ? '<svg class="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>'
                            : ''
                    }
          </div>
        `;

                if (slot.available) {
                    slotButton.addEventListener('click', () => selectSlot(slot.value));
                } else {
                    slotButton.disabled = true;
                }

                slotsContainer.appendChild(slotButton);
            });

            // Show a notice if all slots are booked
            if (!hasAvailableSlots) {
                const noticeDiv = document.createElement('div');
                noticeDiv.className = 'mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-center';
                noticeDiv.innerHTML = '<p class="text-sm text-red-700 m-0">All time slots are fully booked for this date</p>';
                slotsContainer.appendChild(noticeDiv);
            }
        }

        slotSelection.classList.remove('hidden');
        slotPlaceholder.classList.add('hidden');

        updateConfirmButton();
    }

    function selectSlot(slot) {
        selectedSlot = slot;
        renderSlots(selectedDate);
    }

    function updateConfirmButton() {
        const confirmButton = document.getElementById('confirm-booking');
        confirmButton.disabled = !selectedDate || !selectedSlot;
    }

    function confirmBooking() {
        if (!selectedDate || !selectedSlot) return;

        const form = document.querySelector('form[action*="cart"]');
        if (!form) return;

        // Remove any existing booking properties to avoid duplicates
        const existingDateInput = form.querySelector('input[name="properties[Flight Date]"]');
        const existingSlotInput = form.querySelector('input[name="properties[Time Slot]"]');
        const existingTotalInput = form.querySelector('input[name="properties[Total Package Price]"]');
        if (existingDateInput) existingDateInput.remove();
        if (existingSlotInput) existingSlotInput.remove();
        if (existingTotalInput) existingTotalInput.remove();

        // Add new booking properties
        const hiddenDateInput = document.createElement('input');
        hiddenDateInput.type = 'hidden';
        hiddenDateInput.name = 'properties[Flight Date]';
        hiddenDateInput.value = formatDateDisplay(selectedDate);

        const hiddenSlotInput = document.createElement('input');
        hiddenSlotInput.type = 'hidden';
        hiddenSlotInput.name = 'properties[Time Slot]';
        hiddenSlotInput.value = selectedSlot === 'morning' ? 'Morning Flight' : 'Afternoon Flight';

        form.appendChild(hiddenDateInput);
        form.appendChild(hiddenSlotInput);

        // Add total package price if available
        if (bookingData.totalPackagePrice) {
            const hiddenTotalInput = document.createElement('input');
            hiddenTotalInput.type = 'hidden';
            hiddenTotalInput.name = 'properties[Total Package Price]';
            // Convert cents to dollars for display
            const totalInDollars = (bookingData.totalPackagePrice / 100).toFixed(2);
            hiddenTotalInput.value = `$${totalInDollars}`;
            form.appendChild(hiddenTotalInput);
        }

        closeModal();

        // Small delay to ensure modal is closed before submitting
        setTimeout(() => {
            form.requestSubmit();
        }, 100);
    }

    document.querySelector('[data-close-modal]').addEventListener('click', closeModal);

    modal.addEventListener('click', (e) => {
        if (e.target === modal) {
            closeModal();
        }
    });

    document.querySelector('[data-prev-month]').addEventListener('click', async () => {
        currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        await renderCalendar();
    });

    document.querySelector('[data-next-month]').addEventListener('click', async () => {
        currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
        await renderCalendar();
    });

    document.getElementById('confirm-booking').addEventListener('click', confirmBooking);

    window.openBookingModal = openModal;
})();
